import { useState, useEffect, useCallback } from "react";
import { FaSearch, FaEdit, FaPlus, FaRegTrashAlt, FaDownload } from 'react-icons/fa';
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import DataTable from 'react-data-table-component';
import '../common/style.css';
import './project.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { deleteProjectDataByID, showProjects } from "../http/api";
import Loading from "../loading/loading";
import { useAuth } from "../http/authcontex";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { firmReport } from "../http/api";

export default function ShowProject() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const authContext = useAuth();
    const location = useLocation();

    const [financialYear, setFinancialYear] = useState('2026-27');

    useEffect(() => {
        if (location.state?.financialYear) {
            setFinancialYear(location.state.financialYear);
        }
    }, [location.state]);

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        localStorage.getItem('sidebarCollapsed') === 'true'
    );

    const [projects, setProjects] = useState([]);
    const [groupedProjects, setGroupedProjects] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [grandTotal, setGrandTotal] = useState({
        target: 0,
        rez: 0,
        achiv: 0,
        revenue: 0,
        formattedTarget: '0',
        formattedRez: '0',
        formattedAchiv: '0',
        formattedRevenue: '0'
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', isSidebarCollapsed);
    }, [isSidebarCollapsed]);

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
        localStorage.setItem('sidebarCollapsed', !isSidebarCollapsed);
    };

    const fetchProjects = useCallback(() => {
        setLoading(true);
        showProjects(financialYear)
            .then(response => {
                if (response.status === 200) {
                    const projectData = response.data.map(item => ({
                        id: item.id,
                        zone: item.zone || '',
                        type: item.type || '',
                        scheme: item.scheme || '',
                        vertical: item.vertical || '',
                        status: item.status || '',
                        target: item.target || 0,
                        rez: item.rez || 0,
                        achiv: item.achiv || 0,
                        revenue: item.rev || 0,
                        state: item.state || '',
                        remark: item.remark || '',
                        users: item.users || [],
                        activityDetails: item.activityDetails || [],
                        fy: item.fy || '2025-26'
                    }));

                    setProjects(projectData);
                    groupProjects(projectData);
                    setLoading(false);
                }
            })
            .catch(error => {
                setLoading(false);
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized access, redirecting to login...');
                    authContext.logout();
                    navigate('');
                } else {
                    console.error('Error fetching projects:', error);
                }
            });
    }, [authContext, navigate, financialYear]);

    const groupProjects = (projects) => {
        const groups = {};
        let totalTarget = 0;
        let totalRez = 0;
        let totalAchiv = 0;
        let totalRevenue = 0;

        projects.forEach(project => {
            const groupKey = `${project.zone}|${project.type}|${project.vertical}`;

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    zone: project.zone,
                    type: project.type,
                    vertical: project.vertical,
                    totalTarget: 0,
                    totalRez: 0,
                    totalAchiv: 0,
                    totalRevenue: 0,
                    projects: [],
                    isGroup: true
                };
                setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
            }

            groups[groupKey].totalTarget += project.target;
            groups[groupKey].totalRez += project.rez;
            groups[groupKey].totalAchiv += project.achiv;
            groups[groupKey].totalRevenue += project.revenue;
            groups[groupKey].projects.push(project);

            totalTarget += project.target;
            totalRez += project.rez;
            totalAchiv += project.achiv;
            totalRevenue += project.revenue;
        });

        setGrandTotal({
            target: totalTarget,
            rez: totalRez,
            achiv: totalAchiv,
            revenue: totalRevenue,
            formattedTarget: totalTarget.toLocaleString(),
            formattedRez: totalRez.toLocaleString(),
            formattedAchiv: totalAchiv.toLocaleString(),
            formattedRevenue: totalRevenue.toLocaleString()
        });

        const groupedArray = Object.values(groups);
        setGroupedProjects(groupedArray);
        setFilteredProjects(groupedArray);
    };

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        if (searchTerm) {
            const filtered = groupedProjects.map(group => {
                const matchingProjects = group.projects.filter(project =>
                    Object.values(project).some(value =>
                        (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        (typeof value === 'number' && value.toString().includes(searchTerm))
                    )
                );

                if (matchingProjects.length > 0) {
                    return {
                        ...group,
                        projects: matchingProjects,
                        totalTarget: matchingProjects.reduce((sum, p) => sum + p.target, 0),
                        totalRez: matchingProjects.reduce((sum, p) => sum + p.rez, 0),
                        totalAchiv: matchingProjects.reduce((sum, p) => sum + p.achiv, 0),
                        totalRevenue: matchingProjects.reduce((sum, p) => sum + p.revenue, 0)
                    };
                }
                return null;
            }).filter(Boolean);

            setFilteredProjects(filtered);
        } else {
            setFilteredProjects(groupedProjects);
        }
    }, [searchTerm, groupedProjects]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAdd = () => {
        navigate('/addproject', {
            state: { financialYear: financialYear }
        });
    };

    const handleEdit = (projectDetails) => {
        console.log("Navigating to edit project:", projectDetails.id);
        navigate(`/editproject/${projectDetails.id}`, {
            state: {
                projectDetails: projectDetails,
                financialYear: financialYear
            }
        });
    };


    const downloadFirmReport = (projectID, scheme) => {

        setLoading(true);

        let apiCall = firmReport(projectID);
        
        apiCall
            .then((response) => {
                setLoading(false);
                if (response.status === 200) {
                    const byteArray = new Uint8Array(response.data);
                    const contentType =
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                    const blob = new Blob([byteArray], { type: contentType });

                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);

                    const filename = `${scheme}_${projectID}.docx`;

                    link.download = filename;

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    toast.success("Report generated successfully!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                } else {
                    toast.error("Failed to generate the report. Please try again.", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                }
            })
            .catch((error) => {
                setLoading(false);
                if (error.response && error.response.status === 401) {
                    authContext.logout();
                    navigate("/");
                } else {
                    toast.error("Error generating report. Please try again later.", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                }
            });
    };

    const handleDelete = (projectId) => {
        setProjectToDelete(projectId);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (projectToDelete) {
            deleteProjectDataByID(projectToDelete)
                .then((response) => {
                    if (response.status === 200 || response.status === 204) {
                        const updatedProjects = projects.filter(project => project.id !== projectToDelete);
                        setProjects(updatedProjects);
                        groupProjects(updatedProjects);
                        setShowDeleteModal(false);
                        toast.success("Project deleted successfully!", {
                            position: "top-right",
                            autoClose: 3000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                        });

                        const initialExpandedState = {};
                        filteredProjects.forEach(project => {
                            const groupKey = `${project.zone}|${project.type}|${project.vertical}`;
                            initialExpandedState[groupKey] = false;
                        });
                        setExpandedGroups(initialExpandedState);
                    } else {
                        throw new Error("Failed to delete project");
                    }
                })
                .catch((error) => {
                    console.error("Error deleting project:", error);
                    toast.error("Failed to delete project. Please try again.", {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                });
        }
    };

    const formatIndianNumber = (x) => {
        if (x === null || x === undefined) return '0';  // Added guard
        let numStr = x.toString().replace(/[, ]+/g, '');

        const parts = numStr.split('.');
        let integerPart = parts[0];
        const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

        let lastThree = integerPart.substring(integerPart.length - 3);
        let otherNumbers = integerPart.substring(0, integerPart.length - 3);

        if (otherNumbers !== '') {
            lastThree = ',' + lastThree;
        }

        const formattedOtherNumbers = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",");

        return formattedOtherNumbers + lastThree + decimalPart;
    };

    const ExpandedComponent = ({ data }) => {
        return (
            <div className="p-3 bg-light">
                <DataTable
                    columns={[
                        {
                            name: 'Actions',
                            cell: row => (
                                <>
                                    <button
                                        onClick={() => {
                                            console.log("Edit button clicked");
                                            handleEdit(row);
                                        }}
                                        className="btn btn-sm btn-primary me-1"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => {
                                            downloadFirmReport(row.id, row.scheme);
                                        }}
                                        className="btn btn-sm btn-secondary me-1"
                                    >
                                        <FaDownload />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(row.id)}
                                        className="btn btn-sm btn-danger me-1"
                                    >
                                        <FaRegTrashAlt />
                                    </button>
                                </>
                            ),
                            width: '120px'
                        },
                        { name: 'Scheme', selector: row => row.scheme },
                        { name: 'Status', selector: row => row.status },
                        { name: 'Target', selector: row => formatIndianNumber(row.target), right: true },
                        { name: 'Achievement', selector: row => formatIndianNumber(row.achiv), right: true },
                        { name: 'Relization', selector: row => formatIndianNumber(row.rez), right: true },
                        { name: 'Revenue', selector: row => formatIndianNumber(row.revenue), right: true }
                    ]}
                    data={data.projects}
                    noHeader
                />
            </div>
        );
    };

    const columns = [
        {
            name: '',
            cell: row => {
                if (row.isGroup) {
                    return null;
                }
                return null;
            },
            width: '40px'
        },
        {
            name: 'Zone',
            selector: row => row.zone,
            sortable: true,
            cell: row => row.isGroup ? <strong>{row.zone}</strong> : row.zone
        },
        {
            name: 'Type',
            selector: row => row.type,
            sortable: true,
            cell: row => row.isGroup ? <strong>{row.type}</strong> : row.type
        },
        {
            name: 'Vertical',
            selector: row => row.vertical,
            sortable: true,
            cell: row => row.isGroup ? <strong>{row.vertical}</strong> : row.vertical
        },
        {
            name: 'Target',
            selector: row => row.totalTarget ? formatIndianNumber(row.totalTarget) : formatIndianNumber(row.target),
            sortable: true,
            right: true,
            cell: row => row.isGroup ? <strong>{formatIndianNumber(row.totalTarget)}</strong> : formatIndianNumber(row.target)
        },
        {
            name: 'Achievement',
            selector: row => row.totalAchiv ? formatIndianNumber(row.totalAchiv) : formatIndianNumber(row.achiv),
            sortable: true,
            right: true,
            cell: row => row.isGroup ? <strong>{formatIndianNumber(row.totalAchiv)}</strong> : formatIndianNumber(row.achiv)
        },
        {
            name: 'Relization',
            selector: row => row.totalrez !== undefined ? formatIndianNumber(row.totalRez) : formatIndianNumber(row.rez || 0),
            sortable: true,
            right: true,
            cell: row => row.isGroup ? <strong>{formatIndianNumber(row.totalRez)}</strong> : formatIndianNumber(row.rez || 0)
        },
        {
            name: 'Revenue',
            selector: row => row.totalRevenue !== undefined ? formatIndianNumber(row.totalRevenue) : formatIndianNumber(row.revenue || 0),
            sortable: true,
            right: true,
            cell: row => row.isGroup ? <strong>{formatIndianNumber(row.totalRevenue)}</strong> : formatIndianNumber(row.revenue || 0)
        }
    ];

    useEffect(() => {
        if (location.state?.financialYear) {
            setFinancialYear(location.state.financialYear);
        }
    }, [location.state]);

    useEffect(() => {
        const initialExpandedState = {};
        filteredProjects.forEach(project => {
            const groupKey = `${project.zone}|${project.type}|${project.vertical}`;
            initialExpandedState[groupKey] = false;
        });
        setExpandedGroups(initialExpandedState);
    }, [filteredProjects]);

    return (
        <div className="ShowProjects">
            {loading && <Loading />}
            <div className={`sb-nav-fixed ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <TopNav toggleSidebar={toggleSidebar} />
                <div id="layoutSidenav">
                    <SideNave isSidebarCollapsed={isSidebarCollapsed} toggleSidebar={toggleSidebar} />
                    <div id="layoutSidenav_content">
                        <main>
                            <div className="container-fluid px-4">
                                <h4 className="mt-4">Financial Year: {financialYear} </h4>
                                <hr className="bg-warning" style={{ height: '4px' }} />
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            {(authContext.userRoles.includes("ADMIN") || authContext.userRoles.includes("MGR")) &&(
                                            <button
                                                className="btn btn-sm btn-warning me-2"
                                                onClick={handleAdd}
                                                style={{ backgroundColor: '#2d86c8', borderColor: '#2d86c8', color: 'white' }}
                                            >
                                                <FaPlus /> Add
                                            </button>
                                            )}

                                        </div>
                                        <div className="d-flex align-items-center">
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                className="form-control me-2"
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                            <button className="btn btn-sm btn-secondary">
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card mb-4">
                                    <div className="card-body shadow-sm border-0">
                                        <DataTable
                                            className="data-table"
                                            columns={columns}
                                            data={filteredProjects}
                                            pagination
                                            highlightOnHover
                                            paginationPerPage={25}
                                            paginationRowsPerPageOptions={[25, 50, 75, 100]}
                                            expandableRows
                                            expandableRowExpanded={row => {
                                                if (!row.isGroup) return false;
                                                const groupKey = `${row.zone}|${row.type}|${row.vertical}`;
                                                return expandedGroups[groupKey] ?? false; // Default to false (collapsed)
                                            }}
                                            expandableRowsComponent={ExpandedComponent}
                                        />
                                        <div className="grand-total-row mt-3 p-2 bg-light fw-bold">
                                            <div className="row">
                                                <div className="col-md-3">Grand Total</div>
                                                <div className="col-md-2 text-end">{formatIndianNumber(grandTotal.formattedTarget)}</div>
                                                <div className="col-md-2 text-end">{formatIndianNumber(grandTotal.formattedAchiv)}</div>
                                                <div className="col-md-2 text-end">{formatIndianNumber(grandTotal.formattedRez)}</div>
                                                <div className="col-md-2 text-end">{formatIndianNumber(grandTotal.formattedRevenue)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </div>

            {showDeleteModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">Confirm Deletion</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this project? This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
}